/**
 * Plan-Editor (#1138 Tag 3) — Inline-Edit Plan-Werte pro Position × Monat.
 * #1857: zweite Ebene FORECAST (rollierend, versioniert) — der Plan bleibt
 * dort read-only, gepflegt wird nur der Forecast.
 */
import { useEffect, useMemo, useState } from 'react';
import InvestorForecastEditor from './InvestorForecastEditor';

const API = import.meta.env.VITE_API_URL || '';
const MONTHS = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];

function fmtEur(v) {
  if (v === null || v === undefined || v === '') return '–';
  const n = parseFloat(v);
  if (!isFinite(n)) return '–';
  return new Intl.NumberFormat('de-AT', { maximumFractionDigits: 0 }).format(n);
}
function labelize(p) {
  return p.replace(/^guv_|^bilanz_|^cashflow_|^annahmen_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function InvestorPlanEditor() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [bereich, setBereich] = useState('guv');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editKey, setEditKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [ebene, setEbene] = useState('plan'); // 'plan' | 'forecast' (#1857)

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year: String(year), bereich });
      const res = await fetch(`${API}/api/investor/plan-values?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setRows(data.rows || []);
      }
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [year, bereich]); // eslint-disable-line

  const grid = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      if (!map.has(r.position)) map.set(r.position, { position: r.position, cells: {} });
      map.get(r.position).cells[r.monat] = { id: r.id, wert_eur: r.wert_eur };
    }
    return Array.from(map.values()).sort((a, b) => a.position.localeCompare(b.position));
  }, [rows]);

  async function saveCell(id) {
    setSaving(true);
    try {
      const wert = editValue === '' ? 0 : parseFloat(editValue);
      const res = await fetch(`${API}/api/investor/plan/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ wert_eur: wert }),
      });
      if (res.ok) {
        setEditKey(null);
        await load();
      } else {
        const d = await res.json();
        alert('Fehler: ' + (d.error || res.status));
      }
    } finally { setSaving(false); }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-lg font-semibold text-[#1A1A2E]">Plan-Editor</h1>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-[#E5E7EB] overflow-hidden">
            {[['plan', 'Plan'], ['forecast', 'Forecast']].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setEbene(k)}
                className={`text-xs px-3 py-1.5 ${ebene === k ? 'bg-white shadow-sm font-medium text-[#1A1A2E]' : 'bg-[#F8F9FC] text-[#6B7280]'}`}
              >{label}</button>
            ))}
          </div>
          <select value={bereich} onChange={e => setBereich(e.target.value)} className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white">
            <option value="guv">GuV</option>
            <option value="bilanz">Bilanz</option>
            <option value="cashflow">Cashflow</option>
            <option value="annahmen">Annahmen</option>
          </select>
          <select value={year} onChange={e => setYear(parseInt(e.target.value, 10))} className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white">
            {[currentYear, currentYear - 1, currentYear - 2, currentYear + 1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {ebene === 'forecast' ? (
        <InvestorForecastEditor year={year} bereich={bereich} />
      ) : (<>
      <div className="text-xs text-[#6B7280]">
        Klick auf eine Zelle zum Bearbeiten · Enter speichert · Esc bricht ab
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-x-auto">
        <table className="text-sm min-w-full">
          <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
            <tr>
              <th className="text-left px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280] sticky left-0 bg-[#F8F9FC] z-10">Position</th>
              {MONTHS.map((m, i) => (
                <th key={i} className="text-right px-2 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">{m}</th>
              ))}
              <th className="text-right px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">Σ</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={14} className="px-4 py-8 text-center text-xs text-[#9CA3AF]">Lädt…</td></tr>
            ) : grid.length === 0 ? (
              <tr><td colSpan={14} className="px-4 py-8 text-center text-xs text-[#9CA3AF]">Keine Plan-Werte</td></tr>
            ) : grid.map(g => {
              const sum = Object.values(g.cells).reduce((acc, c) => acc + (parseFloat(c.wert_eur) || 0), 0);
              return (
                <tr key={g.position} className="border-t border-[#F1F2F4] hover:bg-[#F8F9FC]/40">
                  <td className="px-3 py-1.5 text-xs text-[#1A1A2E] sticky left-0 bg-white z-10 whitespace-nowrap">{labelize(g.position)}</td>
                  {MONTHS.map((_, i) => {
                    const monat = i + 1;
                    const cell = g.cells[monat];
                    const key = cell ? `${cell.id}` : null;
                    const isEditing = editKey === key;
                    return (
                      <td key={i} className="px-2 py-1 text-right tabular-nums text-xs">
                        {!cell ? (
                          <span className="text-[#E5E7EB]">–</span>
                        ) : isEditing ? (
                          <input
                            type="number"
                            value={editValue}
                            autoFocus
                            disabled={saving}
                            onChange={e => setEditValue(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') saveCell(cell.id);
                              if (e.key === 'Escape') setEditKey(null);
                            }}
                            onBlur={() => saveCell(cell.id)}
                            className="w-20 px-1 py-0.5 border border-[#338edc] rounded text-right text-xs"
                          />
                        ) : (
                          <button
                            onClick={() => { setEditKey(key); setEditValue(cell.wert_eur ?? ''); }}
                            className="text-[#4B5563] hover:text-[#338edc] hover:underline"
                          >
                            {fmtEur(cell.wert_eur)}
                          </button>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-1.5 text-right tabular-nums text-xs font-semibold text-[#1A1A2E]">{fmtEur(sum)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </>)}
    </div>
  );
}
